/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Tabs,
  TimePicker,
} from 'antd';
import moment from 'moment';
import voucherReminderApi from '../gridTemplate/voucherReminderApi';
import VoucherReminderVoucherPicker from '../gridTemplate/VoucherReminderVoucherPicker';
import VoucherReminderTemplateVariables from '../gridTemplate/VoucherReminderTemplateVariables';
import {
  getRejectReason,
  msToScheduleTime,
  normalizeTranslationsInput,
  parseIdList,
  toMs,
  VR_STATUS,
  VR_CHANNEL_OPTIONS,
  VR_CONTENT_LOCALES,
  VR_DEFAULT_CHANNELS,
  VR_SCHEDULE_OPTIONS,
  VR_TRIGGER_OPTIONS,
} from '../gridTemplate/voucherReminderUtils';

export type VoucherReminderFormMode = 'create' | 'edit';

export interface VoucherReminderFormProps {
  mode?: VoucherReminderFormMode;
  id?: number;
  initial?: any;
  /** ListCtrl formModal: { mode, id } */
  query?: { mode?: string; id?: number | string };
  pageInfo?: any;
  onClose?: () => void;
  onSuccess?: () => void;
}

const DEFAULT_VOUCHER_PAGE_ID = 260;

const emptyTranslations = () =>
  VR_CONTENT_LOCALES.map(({ language }) => ({
    language,
    title: '',
    body: '',
  }));

const VoucherReminderFormCtrl = (props: VoucherReminderFormProps) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);

  const mode: VoucherReminderFormMode =
    props.mode ||
    (props.query?.mode === 'update' || props.query?.mode === 'edit'
      ? 'edit'
      : 'create');
  const editId =
    props.id ||
    (props.query?.id != null ? Number(props.query.id) : undefined);

  const voucherPageId =
    Number(
      props.pageInfo?.settings?.grid?.voucherPageId ||
        props.pageInfo?.settings?.voucherPageId ||
        DEFAULT_VOUCHER_PAGE_ID
    ) || DEFAULT_VOUCHER_PAGE_ID;
  const voucherApi =
    props.pageInfo?.settings?.grid?.voucherApi ||
    props.pageInfo?.settings?.voucherApi ||
    'get-voucher';
  const partnerPageId =
    Number(
      props.pageInfo?.settings?.grid?.partnerPageId ||
        props.pageInfo?.settings?.partnerPageId ||
        0
    ) || undefined;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<{ total: number; sample: any[] } | null>(
    null
  );
  const [initial, setInitial] = useState<any>(props.initial || null);

  useEffect(() => {
    const load = async () => {
      if (props.initial) {
        setInitial(props.initial);
        return;
      }
      if (mode === 'edit' && editId) {
        const res = await voucherReminderApi.detail(editId);
        if (res.ok && res.data) setInitial(res.data);
        else
          message.error(res.message || t('voucherReminder.msg.loadCampaignFail'));
      }
    };
    load();
  }, [mode, editId, props.initial]);

  useEffect(() => {
    if (!initial) return;
    const scheduleTimeStr = msToScheduleTime(initial.scheduleTime);
    let translations = normalizeTranslationsInput(initial.translations);
    // Legacy fallback: title/body fields → vi
    if (
      (!translations.find((x) => x.language === 'vi')?.title ||
        !translations.find((x) => x.language === 'vi')?.body) &&
      (initial.title || initial.body)
    ) {
      translations = translations.map((x) =>
        x.language === 'vi'
          ? {
              ...x,
              title: x.title || initial.title || '',
              body: x.body || initial.body || '',
            }
          : x
      );
    }
    form.setFieldsValue({
      name: initial.name,
      voucherIds: parseIdList(initial.voucherIds),
      triggerType: initial.triggerType,
      triggerValue: initial.triggerValue ?? 0,
      channels: (() => {
        const filtered = Array.isArray(initial.channels)
          ? initial.channels.filter((c: string) =>
              VR_CHANNEL_OPTIONS.some((o) => o.value === c)
            )
          : [];
        return filtered.length ? filtered : [...VR_DEFAULT_CHANNELS];
      })(),
      translations,
      deeplink: initial.deeplink,
      scheduleType: initial.scheduleType || 'daily_at',
      scheduleTime: scheduleTimeStr ? moment(scheduleTimeStr, 'HH:mm') : null,
      range:
        toMs(initial.startAt) != null && toMs(initial.endAt) != null
          ? [moment(toMs(initial.startAt)!), moment(toMs(initial.endAt)!)]
          : undefined,
    });
  }, [initial]);

  const buildPayload = (values: any) => {
    const voucherIds = parseIdList(values.voucherIds);
    const startAt = values.range?.[0]?.valueOf();
    const endAt = values.range?.[1]?.valueOf();
    const translations = normalizeTranslationsInput(values.translations).map(
      (item) => ({
        language: item.language,
        title: String(item.title || '').trim(),
        body: String(item.body || '').trim(),
      })
    );
    return {
      name: values.name,
      voucherIds,
      triggerType: values.triggerType,
      triggerValue: Number(values.triggerValue || 0),
      segment: {},
      channels: values.channels?.length
        ? values.channels
        : [...VR_DEFAULT_CHANNELS],
      translations,
      deeplink: values.deeplink || null,
      scheduleType: values.scheduleType,
      scheduleTime:
        values.scheduleType === 'daily_at' && values.scheduleTime
          ? values.scheduleTime.format('HH:mm')
          : null,
      startAt,
      endAt,
    };
  };

  const validateTranslations = (translations: any[]) => {
    const list = normalizeTranslationsInput(translations);
    for (const item of list) {
      if (!item.title || !item.body) {
        return t('voucherReminder.msg.translationsRequired', {
          lang: t(`voucherReminder.lang.${item.language}`),
        });
      }
    }
    if (!list.some((x) => x.language === 'vi' && x.title && x.body)) {
      return t('voucherReminder.msg.translationsNeedVi');
    }
    return null;
  };

  const handlePreview = async () => {
    try {
      const values = await form.validateFields([
        'voucherIds',
        'triggerType',
        'triggerValue',
      ]);
      setPreviewLoading(true);
      const payload = buildPayload({ ...form.getFieldsValue(), ...values });
      const res = await voucherReminderApi.preview({
        voucherIds: payload.voucherIds,
        triggerType: payload.triggerType,
        triggerValue: payload.triggerValue,
        segment: {},
      });
      if (!res.ok || !res.data) {
        message.error(res.message || t('voucherReminder.msg.previewFail'));
        return;
      }
      setPreview(res.data);
    } catch (e) {
      // validation
    } finally {
      setPreviewLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    const payload = buildPayload(values);
    if (!payload.voucherIds.length) {
      message.error(t('voucherReminder.msg.needVoucher'));
      return;
    }
    const transErr = validateTranslations(payload.translations);
    if (transErr) {
      message.error(transErr);
      return;
    }
    if (!payload.startAt || !payload.endAt || payload.endAt <= payload.startAt) {
      message.error(t('voucherReminder.msg.invalidRange'));
      return;
    }
    if (payload.scheduleType === 'daily_at' && !payload.scheduleTime) {
      message.error(t('voucherReminder.msg.needScheduleTime'));
      return;
    }

    setLoading(true);
    try {
      const res =
        mode === 'edit' && editId
          ? await voucherReminderApi.update({ id: editId, ...payload })
          : await voucherReminderApi.create(payload);

      if (!res.ok) {
        message.error(res.message || t('voucherReminder.msg.saveFail'));
        return;
      }
      message.success(
        mode === 'edit'
          ? t('voucherReminder.msg.editOk')
          : t('voucherReminder.msg.createOk')
      );
      props.onSuccess?.();
      props.onClose?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      className="vr-campaign-form"
      initialValues={{
        triggerType: 'expiring_in_days',
        triggerValue: 3,
        channels: [...VR_DEFAULT_CHANNELS],
        scheduleType: 'daily_at',
        voucherIds: [],
        translations: emptyTranslations(),
      }}
      style={{ marginBottom: 0 }}
    >
      {mode === 'edit' && initial?.status === VR_STATUS.REJECTED && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('voucherReminder.detail.rejectedTitle')}
          description={
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <strong>{t('voucherReminder.config.rejectReason')}:</strong>{' '}
              {getRejectReason(initial) || t('voucherReminder.empty')}
            </div>
          }
        />
      )}

      <style>{`
        .vr-campaign-form .ant-form-item { margin-bottom: 10px; }
        .vr-campaign-form .ant-form-item-label { padding: 0 0 2px; }
        .vr-campaign-form .ant-card-head { min-height: 36px; padding: 0 12px; }
        .vr-campaign-form .ant-card-head-title { padding: 8px 0; font-size: 13px; }
        .vr-campaign-form .ant-card-body { padding: 12px; }
        .vr-campaign-form .ant-tabs-bar { margin-bottom: 8px; }
      `}</style>

      <Row gutter={16}>
        {/* Cột trái: cấu hình */}
        <Col span={12}>
          <Card size="small" title={t('voucherReminder.form.section.info')}>
            <Form.Item
              name="name"
              label={t('voucherReminder.form.name')}
              rules={[
                { required: true, message: t('voucherReminder.form.required') },
              ]}
            >
              <Input
                placeholder={t('voucherReminder.form.name.placeholder')}
                maxLength={255}
              />
            </Form.Item>
            <Form.Item
              name="voucherIds"
              label={t('voucherReminder.form.voucherIds')}
              rules={[
                {
                  required: true,
                  type: 'array',
                  min: 1,
                  message: t('voucherReminder.form.voucherIds.required'),
                },
              ]}
            >
              <VoucherReminderVoucherPicker
                voucherPageId={voucherPageId}
                voucherApi={voucherApi}
                partnerPageId={partnerPageId}
              />
            </Form.Item>
            <Form.Item name="deeplink" label={t('voucherReminder.form.deeplink')}>
              <Input maxLength={1000} placeholder="umoney://..." />
            </Form.Item>
          </Card>

          <Card
            size="small"
            title={t('voucherReminder.form.section.audience')}
            style={{ marginTop: 12 }}
          >
            <Row gutter={12}>
              <Col span={14}>
                <Form.Item
                  name="triggerType"
                  label={t('voucherReminder.form.triggerType')}
                  rules={[{ required: true }]}
                >
                  <Select>
                    {VR_TRIGGER_OPTIONS.map((o) => (
                      <Select.Option key={o.value} value={o.value}>
                        {t(o.labelId)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="triggerValue"
                  label={t('voucherReminder.form.triggerValue')}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Space size="small" wrap>
              <Button size="small" onClick={handlePreview} loading={previewLoading}>
                {t('voucherReminder.action.preview')}
              </Button>
              {preview && (
                <Alert
                  type="info"
                  showIcon
                  message={t('voucherReminder.form.preview.estimate', {
                    total: preview.total,
                  })}
                  style={{ marginBottom: 0, padding: '2px 8px' }}
                />
              )}
            </Space>
          </Card>

          <Card
            size="small"
            title={t('voucherReminder.form.section.delivery')}
            style={{ marginTop: 12 }}
          >
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="channels"
                  label={t('voucherReminder.form.channels')}
                  rules={[
                    {
                      required: true,
                      message: t('voucherReminder.form.channels.required'),
                    },
                  ]}
                >
                  <Select mode="multiple">
                    {VR_CHANNEL_OPTIONS.map((o) => (
                      <Select.Option key={o.value} value={o.value}>
                        {t(o.labelId)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="scheduleType"
                  label={t('voucherReminder.form.scheduleType')}
                  rules={[{ required: true }]}
                >
                  <Select>
                    {VR_SCHEDULE_OPTIONS.map((o) => (
                      <Select.Option key={o.value} value={o.value}>
                        {t(o.labelId)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    prev.scheduleType !== cur.scheduleType
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('scheduleType') === 'daily_at' ? (
                      <Form.Item
                        name="scheduleTime"
                        label={t('voucherReminder.form.scheduleTime')}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'voucherReminder.form.scheduleTime.required'
                            ),
                          },
                        ]}
                      >
                        <TimePicker format="HH:mm" style={{ width: '100%' }} />
                      </Form.Item>
                    ) : (
                      <div />
                    )
                  }
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item
                  name="range"
                  label={t('voucherReminder.form.range')}
                  rules={[
                    {
                      required: true,
                      message: t('voucherReminder.form.range.required'),
                    },
                  ]}
                >
                  <DatePicker.RangePicker
                    showTime
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY HH:mm"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Cột phải: đa ngôn ngữ */}
        <Col span={12}>
          <Card
            size="small"
            title={t('voucherReminder.form.content')}
            extra={<VoucherReminderTemplateVariables />}
            style={{ height: '100%' }}
          >
            <Form.List name="translations">
              {(fields) => (
                <Tabs type="card" size="small">
                  {fields.map((field) => {
                    const lang =
                      form.getFieldValue([
                        'translations',
                        field.name,
                        'language',
                      ]) || VR_CONTENT_LOCALES[field.name]?.language;
                    return (
                      <Tabs.TabPane
                        tab={t(`voucherReminder.lang.${lang}`)}
                        key={lang || field.key}
                        forceRender
                      >
                        <Form.Item name={[field.name, 'language']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'title']}
                          label={t('voucherReminder.form.title')}
                          rules={[
                            {
                              required: true,
                              message: t('voucherReminder.form.required'),
                            },
                          ]}
                        >
                          <Input maxLength={255} />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'body']}
                          label={t('voucherReminder.form.body')}
                          rules={[
                            {
                              required: true,
                              message: t('voucherReminder.form.required'),
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input.TextArea rows={8} />
                        </Form.Item>
                      </Tabs.TabPane>
                    );
                  })}
                </Tabs>
              )}
            </Form.List>
          </Card>
        </Col>
      </Row>

      <Space
        style={{
          width: '100%',
          justifyContent: 'flex-end',
          marginTop: 12,
          paddingTop: 8,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        {props.onClose && (
          <Button onClick={props.onClose} disabled={loading}>
            {t('voucherReminder.action.cancel')}
          </Button>
        )}
        <Button type="primary" htmlType="submit" loading={loading}>
          {mode === 'edit'
            ? t('voucherReminder.action.saveEdit')
            : t('voucherReminder.action.saveCreate')}
        </Button>
      </Space>
    </Form>
  );
};

export default VoucherReminderFormCtrl;
